import React, { createContext, useContext, useMemo, useState } from "react";
import {
  useTSQWorldLocationsCountries,
  useTSQWorldLocationsCities,
  useTSQWorldLocationsStates,
} from "../hooks/useTSAddresses";
import {
  WorldLocationResponse,
  WorldCountrySchema,
  WorldStateSchema,
  WorldCitySchema,
} from "../types/addressTypes";
import { PaginatedResponse } from "../types/common/commonTypes";
import { UseQueryResult } from "@tanstack/react-query";

export interface WorldLocationContextValue {
  countries: UseQueryResult<PaginatedResponse<WorldCountrySchema>, Error>;
  states: UseQueryResult<PaginatedResponse<WorldStateSchema>, Error>;
  cities: UseQueryResult<PaginatedResponse<WorldCitySchema>, Error>;
  anyLoading: boolean;
  anyError: boolean;
}

const WorldLocationContext = createContext<
  WorldLocationContextValue | undefined
>(undefined);

export function WorldLocationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const country = useTSQWorldLocationsCountries(true);
  const state = useTSQWorldLocationsStates(true);
  const city = useTSQWorldLocationsCities(true);

  const contextValue: WorldLocationContextValue = useMemo(() => {
    const anyLoading = country.isLoading || state.isLoading || city.isLoading;
    const anyError = !!country.error || !!state.error || !!city.error;
    return {
      countries: country,
      states: state,
      cities: city,
      anyLoading,
      anyError,
    };
  }, [city, state, country]);

  return (
    <WorldLocationContext.Provider value={contextValue}>
      {children}
    </WorldLocationContext.Provider>
  );
}

export function useWorldLocationData() {
  const context = useContext(WorldLocationContext);
  if (context === undefined) {
    throw new Error(
      "useLocationData must be used with a WorldLocationProvider",
    );
  }
  return context;
}
